import { useEffect, useState } from "react";
import { createUseStyles } from "react-jss";
import produce from "immer";
import { fetchApi } from "../../../services/api";
import Label from "../../refresh/Label";
import RedButton from "../../refresh/RedButton";
import SidebarLightbox from "../../refresh/SidebarLightbox";
import GenericOptions from "../GenericOptions";
import styles from "./sharedStyles";
import Checkbox from "../../refresh/inputs/Checkbox";
import { useGallerySelector } from "../../refresh/selectors/GallerySelector";

const useStyles = createUseStyles({
  ...styles,
  galleries: {
    "margin-top": 15,
    height: 270,
    "overflow-y": "auto",
  },
  gallery: {
    width: "100%",
    "box-sizing": "border-box",
    cursor: "pointer",
    transition: "background-color 0.2s ease",
    padding: [5, 5],
    "&:hover": {
      "background-color": "#373737",
    },
    "border-radius": 3,
  },
  name: {
    color: "white",
    margin: 0,
  },
  date: {
    color: "#707070",
    margin: [5, 0, 0],
  },
  preview: {
    width: "100%",
    "margin-top": 15,
    "overflow-x": "auto",
    "overflow-y": "hidden",
    display: "flex",
    "flex-direction": "row",

    "& img": {
      height: 100,
      margin: [0, 5, 5, 0],
      "border-radius": 5,
    },
  },
  checkboxes: {
    display: "flex",
  },
});

const EditGalleryHotspot = ({
  show,
  hotspot,
  onClose,
  onComplete,
  account,
  isLink,
  setLink,
  template = false,
}) => {
  const classes = useStyles();
  const [config, setConfig] = useState(
    produce(hotspot, draft => {
      draft.options.fit = "fit";
    }),
  );
  const [gallery, getGallery] = useGallerySelector({});
  const [images, setImages] = useState([]);

  const id = config.options?.gallery?.id;

  useEffect(() => {
    if (gallery.id) {
      setImages(
        Array.from({ length: 4 }, (_, id) => ({
          id,
          preview_url:
            "data:image/gif;base64,R0lGODlhAQABAIAAAMLCwgAAACH5BAAAAAAALAAAAAABAAEAAAICRAEAOw==",
        })),
      );
      setConfig(
        produce(draft => {
          draft.options.gallery = {
            id: gallery.id,
            title: gallery.name,
          };
        }),
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gallery.id]);

  useEffect(() => {
    if (id) {
      fetchApi(`/gallery/${id}`, { version: "v2" })
        .then(res => setImages(res.entries))
        .catch(err => console.error(err));
    }
  }, [id]);

  return (
    <SidebarLightbox
      show={show}
      onClose={onClose}
      sidebar={
        <>
          <h1 className={classes.title}>
            Edit Gallery {template ? "Template" : "Popup"}
          </h1>
          <p className={classes.subtitle}>
            Display a collection of images from the CMS
          </p>
          <div className={classes.grow} />
          {!template && (
            <Checkbox sidebar value={isLink} onChange={setLink}>
              Show in Links menu
            </Checkbox>
          )}
        </>
      }
    >
      <div className={classes.options}>
        <GenericOptions
          icon="Photo"
          config={config}
          setConfig={setConfig}
          account={account}
          template={template}
        />
        <hr />
        <Label name="Gallery" required>
          <div className={classes.genericSelector}>
            <p>{config.options?.gallery?.title || "Select a gallery"}</p>
            <button onClick={() => getGallery()}>Select from CMS</button>
          </div>
        </Label>
        <div className={classes.preview}>
          {images.map(image => (
            <img key={image.id} src={image.preview_url} alt={image.caption} />
          ))}
        </div>
        <div className={classes.checkboxes}>
          <Checkbox
            value={config.options.fit === "fit"}
            onChange={fit =>
              setConfig(
                produce(draft => {
                  draft.options.fit = fit ? "fit" : "fill";
                }),
              )
            }
          >
            Scale images to fit
          </Checkbox>
          <Checkbox
            value={config.options.fit === "fill"}
            onChange={fit =>
              setConfig(
                produce(draft => {
                  draft.options.fit = fit ? "fill" : "fit";
                }),
              )
            }
          >
            Scale images to fill
          </Checkbox>
        </div>
        <RedButton
          disabled={
            !(
              config.tooltip &&
              config.options?.title &&
              config.options?.gallery
            )
          }
          onClick={() => onComplete(config)}
        >
          Save
        </RedButton>
      </div>
    </SidebarLightbox>
  );
};

export default EditGalleryHotspot;
